import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { SxProps, Theme, Typography } from '@mui/material'
import CookieIcon from '@mui/icons-material/Cookie'
import { useCookieConsentStore } from '../store/cookie-consent.store'
import { useLocalizationStore } from '../../localization/store/localization.store'
import { CookieSettingsModal } from './CookieSettingsModal'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { AppBaseLinkKind } from 'src/component/common/analog/molecule/link/AppBaseLinkKind'
import { companyLinks } from 'src/util/link'

export const CookieConsentBanner = observer(() => {
  const cookieConsentStore = useCookieConsentStore()
  const { translation } = useLocalizationStore()
  const [showSettings, setShowSettings] = useState(false)

  if (!cookieConsentStore.shouldShowBanner) {
    return null
  }

  const texts = translation.cookieConsent.banner

  const handleOpenTerms = () => {
    window.open(companyLinks.website.terms, '_blank', 'noopener,noreferrer')
  }

  return (
    <>
      <AppPaper sx={styles.banner}>
        <AppBox sx={styles.content}>
          <AppBox sx={styles.header}>
            <CookieIcon sx={styles.icon} />
            <Typography sx={styles.title}>{texts.title()}</Typography>
          </AppBox>
          <Typography sx={styles.description}>
            {texts.description()}{' '}
            <AppBaseLinkKind
              fontSize="inherit"
              kind="buttonLikeLink"
              underline="always"
              textValue={texts.privacyPolicy()}
              onClick={handleOpenTerms}
              sx={{ display: 'inline' }}
            />
          </Typography>
        </AppBox>
        <AppBox sx={styles.buttons}>
          <AppButton
            variant="contained"
            onClick={() => cookieConsentStore.acceptAll()}
            textValue={texts.acceptAll()}
            fontSize={12}
            fontWeight={500}
          />
          <AppButton
            variant="light"
            onClick={() => cookieConsentStore.acceptNecessaryOnly()}
            textValue={texts.rejectAll()}
            fontSize={12}
            fontWeight={500}
          />
          <AppButton
            variant="light"
            onClick={() => setShowSettings(true)}
            textValue={texts.customize()}
            fontSize={12}
            fontWeight={500}
          />
        </AppBox>
      </AppPaper>

      <CookieSettingsModal open={showSettings} onClose={() => setShowSettings(false)} />
    </>
  )
})

const styles: Record<string, SxProps<Theme>> = {
  banner: {
    position: 'fixed',
    bottom: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    maxWidth: { xs: '95%', sm: '60%', md: '50%', lg: '40%' },
    width: '100%',
    padding: '10px 15px',
    display: 'flex',
    flexDirection: 'column',
    gap: 15,
    borderRadius: '10px',
    zIndex: 9999,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  icon: {
    color: 'warning.main',
    fontSize: 18,
  },
  title: {
    fontWeight: 600,
    fontSize: { xs: 14, sm: 16 },
    color: 'text.primary',
  },
  description: {
    fontSize: { xs: 11, sm: 12 },
    color: 'text.secondary',
  },
  buttons: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
    gap: 10,
  },
}
